// /admin/configuracion-ia
// Editor del contexto de negocio que la IA usa al analizar reportes.
// Se guarda en app_config.ai_business_context.

import { useEffect, useState } from 'react'
import AdminLayout from '../../components/AdminLayout'
import { supabase } from '../../lib/supabase'

const KEY = 'ai_business_context'

export default function ConfiguracionIA() {
  const [contexto, setContexto] = useState('')
  const [original, setOriginal] = useState('')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [msg, setMsg] = useState(null)
  const [error, setError] = useState(null)

  useEffect(() => { cargar() }, [])

  async function cargar() {
    setLoading(true)
    try {
      const { data, error } = await supabase
        .from('app_config').select('value').eq('key', KEY).maybeSingle()
      if (error) throw error
      setContexto(data?.value || '')
      setOriginal(data?.value || '')
    } catch (e) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }

  async function guardar() {
    setSaving(true)
    setMsg(null)
    setError(null)
    try {
      // Upsert: si existe update, si no insert
      const { error } = await supabase
        .from('app_config')
        .upsert({ key: KEY, value: contexto }, { onConflict: 'key' })
      if (error) throw error
      setOriginal(contexto)
      setMsg('✓ Guardado. La IA va a usar este contexto en el próximo análisis.')
      setTimeout(() => setMsg(null), 5000)
    } catch (e) {
      setError(e.message)
    } finally {
      setSaving(false)
    }
  }

  function resetearDefault() {
    if (!confirm('¿Volver al contexto por defecto de Limpack? Vas a perder los cambios.')) return
    setContexto(DEFAULT_CONTEXT)
  }

  const cambios = contexto !== original
  const chars = contexto.length
  const tokens = Math.ceil(chars / 4)  // estimación rough

  return (
    <AdminLayout>
      <div className="page">
        <h1>🤖 Configuración IA</h1>
        <p className="muted">
          Este texto se le pasa a Claude en cada análisis de reporte. Sirve para que la IA entienda en qué etapa está Limpack,
          qué sistemas ya tenés montados, y no te dé consejos genéricos de manual.
        </p>

        <div className="ci-grid">
          <div className="ci-main">
            <div className="ci-head">
              <div>
                <strong>Contexto del negocio</strong>
                <div style={{ fontSize: 11, color: '#6b7280' }}>
                  {chars.toLocaleString('es-UY')} caracteres · ~{tokens.toLocaleString('es-UY')} tokens (cada análisis paga este costo + el reporte)
                </div>
              </div>
              <button className="ci-btn-link" onClick={resetearDefault}>↺ Resetear a default</button>
            </div>

            {loading && <div className="ci-loading">Cargando…</div>}

            {!loading && (
              <textarea
                className="ci-textarea"
                value={contexto}
                onChange={e => setContexto(e.target.value)}
                placeholder="Describí tu negocio: modelo, etapa, qué tenés ya montado, qué falta, fundadores, etc."
                rows={28}
              />
            )}

            {error && <div className="ci-error"><strong>Error:</strong> {error}</div>}
            {msg && <div className="ci-success">{msg}</div>}

            <div className="ci-actions">
              <button
                className="ci-btn-primary"
                onClick={guardar}
                disabled={saving || !cambios || loading}
              >
                {saving ? 'Guardando…' : cambios ? 'Guardar cambios' : 'Sin cambios'}
              </button>
              {cambios && (
                <button className="ci-btn-secondary" onClick={() => setContexto(original)} disabled={saving}>
                  Descartar
                </button>
              )}
            </div>
          </div>

          <aside className="ci-side">
            <div className="ci-card">
              <h3>💡 Tips para escribir buen contexto</h3>
              <ul>
                <li><strong>Etapa:</strong> pre-launch, launching, growth, mature</li>
                <li><strong>Modelo:</strong> qué vendés, cómo cobrás, frecuencia</li>
                <li><strong>Lo que YA tenés:</strong> integraciones técnicas funcionando (para que no te las recomiende)</li>
                <li><strong>Lo que falta:</strong> bloqueos actuales, próximos pasos</li>
                <li><strong>Stack de datos:</strong> qué representa cada tabla (ej: clientes = suscripciones, no usuarios)</li>
                <li><strong>Instrucciones explícitas:</strong> "NO recomiendes X", "SÍ enfocate en Y"</li>
              </ul>
            </div>

            <div className="ci-card">
              <h3>📊 Metadata automática</h3>
              <p style={{ fontSize: 12, color: '#6b7280' }}>
                Además del contexto que escribas acá, la IA recibe automáticamente en cada análisis:
              </p>
              <ul>
                <li>Cantidad de productos activos</li>
                <li>Cantidad de suscriptores activos</li>
                <li>Cantidad de barrios activos</li>
                <li>Cantidad de porteros activos</li>
                <li>Fecha del análisis</li>
              </ul>
              <p style={{ fontSize: 12, color: '#6b7280' }}>
                Esto le da a Claude un snapshot del estado real para complementar tu contexto narrativo.
              </p>
            </div>

            <div className="ci-card">
              <h3>💰 Costo</h3>
              <p style={{ fontSize: 12, color: '#6b7280' }}>
                Sonnet 4.5 a $3/Mtok input, $15/Mtok output.<br/>
                Análisis típico ≈ 500 in + 500 out = <strong>~$0.009 USD</strong> (~0,40 UYU).<br/>
                Contextos largos suben el costo del input proporcionalmente.
              </p>
            </div>
          </aside>
        </div>
      </div>

      <style>{`
        .ci-grid { display: grid; grid-template-columns: 1fr 320px; gap: 20px; align-items: flex-start; }
        @media (max-width: 900px) { .ci-grid { grid-template-columns: 1fr; } }
        .ci-main { background: #fff; border: 1px solid #e5e7eb; border-radius: 12px; padding: 18px; }
        .ci-head { display: flex; justify-content: space-between; align-items: center; gap: 10px; margin-bottom: 10px; flex-wrap: wrap; }
        .ci-textarea {
          width: 100%; box-sizing: border-box;
          font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
          font-size: 13px; line-height: 1.6;
          padding: 14px;
          border: 1px solid #d1d5db; border-radius: 8px;
          background: #f8fafc;
          resize: vertical;
        }
        .ci-textarea:focus { outline: none; border-color: #1a3461; box-shadow: 0 0 0 3px rgba(26,52,97,.1); }
        .ci-actions { display: flex; gap: 8px; margin-top: 12px; }
        .ci-btn-primary {
          padding: 10px 20px; background: #1a3461; color: #fff;
          border: 0; border-radius: 8px; font-weight: 700; font-size: 14px;
          cursor: pointer;
        }
        .ci-btn-primary:disabled { opacity: .5; cursor: not-allowed; }
        .ci-btn-secondary {
          padding: 10px 18px; background: #fff; color: #475569;
          border: 1px solid #d1d5db; border-radius: 8px; font-weight: 600; font-size: 14px;
          cursor: pointer;
        }
        .ci-btn-link { background: none; border: 0; color: #1a3461; font-size: 12px; cursor: pointer; text-decoration: underline; }
        .ci-loading { padding: 40px; text-align: center; color: #6b7280; }
        .ci-error { margin-top: 10px; padding: 10px 12px; background: #fee2e2; border: 1px solid #fca5a5; border-radius: 8px; color: #991b1b; font-size: 13px; }
        .ci-success { margin-top: 10px; padding: 10px 12px; background: #d1fae5; border: 1px solid #a7f3d0; border-radius: 8px; color: #065f46; font-size: 13px; font-weight: 600; }
        .ci-side { display: flex; flex-direction: column; gap: 12px; }
        .ci-card { background: #fff; border: 1px solid #e5e7eb; border-radius: 10px; padding: 14px; }
        .ci-card h3 { margin: 0 0 8px; font-size: 13px; color: #1a3461; }
        .ci-card ul { margin: 0; padding-left: 20px; font-size: 12px; line-height: 1.6; color: #374151; }
        .ci-card li { margin-bottom: 4px; }
      `}</style>
    </AdminLayout>
  )
}

const DEFAULT_CONTEXT = `**Limpack** es un B2C de suscripciones mensuales de productos de limpieza en Montevideo, Uruguay.

**Modelo de negocio:**
- Suscripciones recurrentes mensuales (kit personalizable según composición del hogar)
- Pedidos one-shot: kit suelto o productos sueltos en La Tienda
- Programa de Vecinos/Porteros: referidos por edificio con comisión

**Estado actual (etapa temprana - pre-launch):**
- Sistema técnico: TODO INTEGRADO Y FUNCIONANDO. Mercado Pago (preapproval para suscripciones + Payment Brick para one-shot), Supabase con auth/email verification, panel de admin completo, módulo de logística (entregas, etiquetas térmicas Xprinter, recordatorios WA), tienda pública.
- Modo: actualmente en TEST con cuenta MP de Roberto Monti (figura comercial inicial).
- Lo que falta: subir el catálogo completo de productos y conseguir los primeros suscriptores reales.
- Datos en DB son mayormente de TEST/desarrollo.

**Fundadores/operación:**
- Nico Jaime: dueño/PM/desarrollo (Jaime Consultancy hace el producto)
- Roberto Monti: persona física que aporta cuenta MP inicial
- A futuro: SAS con cuenta bancaria propia

**Stack técnico relevante para entender datos:**
- Tabla "clientes" = suscripciones (no usuarios). Una persona puede tener N suscripciones (titular/beneficiario).
- Tabla "pedidos" = compras one-shot (kit único o productos sueltos).
- "pagos" = cobros recurrentes de suscripciones.
- "comisiones" = lo que se debe al portero por referidos.
- Modo cobro: prepaid_mensiversario (cobra el día del aniversario de alta cada mes).

**IMPORTANTE para tus análisis:**
- NO recomiendes "implementar gateway de pagos" ni "activar suscripciones" — YA está hecho técnicamente.
- SÍ es válido recomendar: subir productos, hacer marketing, captación de suscriptores, ajustes de precio, optimización operativa, etc.
- Si los datos están en cero, contextualizá: "estás en pre-launch, lo que necesitás es X".
- Pensá en accionables CONCRETOS para esta etapa, no consejos genéricos.`
